import type { FunctionComponent } from 'react';
import { Fragment } from 'react';
import { FormattedMessage } from 'react-intl';

import type { PIIString } from '@trello/privacy';

import { Joinn } from 'app/src/components/Joinn';

import * as styles from './ReactionTooltip.module.less';

/**
 * TooltipContent renders the string "{names} reacted with {shortcodes}."
 */
interface TooltipContentProps {
  isMyReaction: boolean;
  shortcodes?: JSX.Element;
  names: JSX.Element[];
}

const TooltipContent: FunctionComponent<TooltipContentProps> = ({
  isMyReaction,
  names,
  shortcodes,
}) => {
  if (!shortcodes) {
    return <Joinn key="names" components={names} />;
  } else if (isMyReaction && names.length === 1) {
    return (
      <FormattedMessage
        id="templates.reactions.reaction-tooltip-you"
        defaultMessage="You reacted with {reaction}"
        description="Tooltip for reactions where the current user is the only one reacting"
        values={{
          reaction: shortcodes,
        }}
      />
    );
  } else if (names.length === 1) {
    return (
      <FormattedMessage
        id="templates.reactions.reaction-tooltip"
        defaultMessage="{name} reacted with {reaction}"
        description="Tooltip for reactions where only one person is reacting"
        values={{
          name: names[0],
          reaction: shortcodes,
        }}
      />
    );
  } else if (names.length > 1) {
    return (
      <FormattedMessage
        id="templates.reactions.reaction-tooltip-plural"
        defaultMessage="{names} reacted with {reaction}"
        description="Tooltip for reactions where multiple people are reacting"
        values={{
          names: <Joinn key="names" components={names} />,
          reaction: shortcodes,
        }}
      />
    );
  } else {
    return shortcodes;
  }
};

/**
 * ReactionsTooltip renders a string like "You, Name2 and Name3 reacted with
 * :joy:"
 */
interface ReactionTooltipProps {
  myId: string;
  shortcodes: string;
  members: {
    id: string;
    fullName?: PIIString;
  }[];
}

export const ReactionTooltip: FunctionComponent<ReactionTooltipProps> = ({
  members,
  shortcodes,
  myId,
}) => {
  const isMyReaction = members.some((member) => member.id === myId);
  const otherMembers = members.filter((member) => member.id !== myId);

  const names = [
    ...(isMyReaction
      ? [
          <Fragment key="me">
            <FormattedMessage id="You" defaultMessage="You" description="You" />
          </Fragment>,
        ]
      : []),
    ...otherMembers.map((member) => (
      <span className={styles.name} key={member.id}>
        {member.fullName}
      </span>
    )),
  ];

  return (
    <span>
      <TooltipContent
        isMyReaction={isMyReaction}
        shortcodes={
          shortcodes ? (
            <span key="shortcodes" className={styles.shortcodes}>
              {shortcodes}
            </span>
          ) : undefined
        }
        names={names}
      />
    </span>
  );
};
